import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Smartphone, Monitor, Tablet } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';

interface DeviceData {
  device: string;
  count: number;
}

interface DeviceBreakdownChartProps {
  data: DeviceData[];
  totalScans: number;
  className?: string;
}

const DEVICE_COLORS: Record<string, string> = {
  mobile: '#101F1C',
  desktop: '#2D3E3A',
  tablet: '#5C6E6A',
  default: '#A8B8B4',
};

const chartConfig = {
  mobile: { label: "Mobile", color: DEVICE_COLORS.mobile },
  desktop: { label: "Desktop", color: DEVICE_COLORS.desktop },
  tablet: { label: "Tablet", color: DEVICE_COLORS.tablet },
};

const getDeviceIcon = (device: string) => {
  switch (device.toLowerCase()) {
    case 'mobile': return Smartphone;
    case 'tablet': return Tablet;
    default: return Monitor;
  }
};

export function DeviceBreakdownChart({ data, totalScans, className = '' }: DeviceBreakdownChartProps) {
  const chartData = data.map((item) => ({
    name: item.device.charAt(0).toUpperCase() + item.device.slice(1),
    value: item.count,
    percentage: totalScans > 0 ? ((item.count / totalScans) * 100).toFixed(1) : '0',
    color: DEVICE_COLORS[item.device.toLowerCase()] || DEVICE_COLORS.default,
  }));

  if (!data || data.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[--clinqr-text-primary] flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[--clinqr-navy]" />
            Device Breakdown
          </h3>
        </div>
        <div className="flex items-center justify-center h-48 bg-[--clinqr-bg-subtle] rounded-lg border border-dashed border-[--clinqr-border]/10">
          <div className="text-center">
            <Smartphone className="w-10 h-10 text-[--clinqr-text-muted] mx-auto mb-2" />
            <p className="text-sm text-[--clinqr-text-secondary]">No device data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Find dominant device
  const dominant = chartData.reduce((prev, curr) =>
    parseInt(curr.percentage as string) > parseInt(prev.percentage as string) ? curr : prev
  );

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[--clinqr-text-primary] flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-[--clinqr-navy]" />
          Device Breakdown
        </h3>
      </div>
      <p className="text-xs text-[--clinqr-text-secondary] mb-3">
        {dominant.name} leads ({dominant.percentage}%)
      </p>

      <div className="flex items-center gap-4">
        {/* Pie Chart */}
        <div className="relative w-32 h-32 flex-shrink-0">
          <ChartContainer config={chartConfig} className="w-32 h-32">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={28}
                outerRadius={52}
                paddingAngle={2}
                dataKey="value"
                animationBegin={200}
                animationDuration={1200}
                animationEasing="ease-out"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, name) => [`${value} scans`, name]}
                  />
                }
              />
            </PieChart>
          </ChartContainer>
          {/* Center Label */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-sm font-bold text-[--clinqr-text-primary]">{totalScans}</div>
              <div className="text-[10px] text-[--clinqr-text-muted]">Total</div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2">
          {chartData.map((item) => {
            const Icon = getDeviceIcon(item.name);
            return (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <Icon className="w-3.5 h-3.5 text-[--clinqr-text-muted]" />
                  <span className="text-xs text-[--clinqr-text-primary]">{item.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-[--clinqr-text-primary]">{item.value}</span>
                  <span className="text-xs text-[--clinqr-text-secondary] ml-1">({item.percentage}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress bars */}
      <div className="mt-3 space-y-1.5">
        {chartData.map((item) => (
          <div key={item.name} className="w-full bg-[--clinqr-bg-subtle] rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                backgroundColor: item.color,
                width: `${item.percentage}%`
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
